import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../services/supabaseClient'
import { useAuth } from '../context/AuthContext'

const feeTypeLabels = {
  registration: 'رسوم تسجيل',
  monthly_tuition: 'قسط شهري',
  transport: 'نقل',
  other: 'أخرى',
}

const feeStatusLabels = {
  unpaid: 'غير مسدد',
  partial: 'مسدد جزئياً',
  paid: 'مسدد بالكامل',
  waived: 'معفى',
  cancelled: 'ملغى',
}

function formatAmount(v) {
  const num = v != null ? Number(v) || 0 : 0
  if (!num) return '—'
  return num.toLocaleString('en-US')
}

export default function Fees() {
  const { user, signOut } = useAuth()
  const email = user?.email || ''
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [fees, setFees] = useState([])
  const [payments, setPayments] = useState([])

  useEffect(() => {
    let active = true
    ;(async () => {
      setLoading(true)
      setError('')
      try {
        const [feesRes, paymentsRes] = await Promise.all([
          supabase.rpc('get_kg_kid_fees_overview'),
          supabase.rpc('get_kg_kid_fee_payments'),
        ])
        if (!active) return
        if (feesRes.error) throw feesRes.error
        if (paymentsRes.error) throw paymentsRes.error
        setFees(feesRes.data || [])
        setPayments(paymentsRes.data || [])
      } catch (e) {
        if (!active) return
        setError(e.message || 'تعذر تحميل بيانات الرسوم')
        setFees([])
        setPayments([])
      } finally {
        if (!active) return
        setLoading(false)
      }
    })()
    return () => { active = false }
  }, [])

  const summary = useMemo(() => {
    if (!fees || fees.length === 0) {
      return { totalFees: 0, totalPaid: 0, remaining: 0, status: 'لا توجد رسوم مسجلة حتى الآن.' }
    }
    let totalFees = 0
    let totalPaid = 0
    fees.forEach(f => {
      totalFees += f.amount != null ? Number(f.amount) || 0 : 0
      totalPaid += f.total_paid_for_fee != null ? Number(f.total_paid_for_fee) || 0 : 0
    })
    const remainingRaw = totalFees - totalPaid
    const remaining = remainingRaw > 0 ? remainingRaw : 0
    let status = 'غير مسدد'
    if (totalFees === 0) status = 'لا توجد رسوم'
    else if (remaining <= 0) status = 'مسدد بالكامل'
    else if (totalPaid <= 0) status = 'غير مسدد'
    else status = 'مسدد جزئياً'
    return { totalFees, totalPaid, remaining, status }
  }, [fees])

  const hasFees = fees && fees.length > 0
  const hasPayments = payments && payments.length > 0

  return (
    <div
      dir="rtl"
      style={{
        minHeight: '100vh',
        backgroundColor: '#f3f4f6',
        padding: '1.5rem',
      }}
    >
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: 16,
            padding: 16,
            boxShadow: '0 10px 25px rgba(15,23,42,0.08)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{ margin: 0 }}>رسوم رياض الأطفال</h1>
            <button
              type="button"
              onClick={signOut}
              style={{
                padding: '6px 10px',
                borderRadius: 999,
                border: '1px solid #e5e7eb',
                background: '#f9fafb',
                cursor: 'pointer',
                fontSize: 13,
              }}
            >
              تسجيل الخروج
            </button>
          </div>

          <div style={{ color: '#6b7280', marginTop: 4, marginBottom: 12, fontSize: 14 }}>
            عرض ملخص الرسوم والمدفوعات المتبقية لطفلك في رياض الأطفال.
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => navigate('/')}
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
              >
                لوحة المعلومات
              </button>
              <button
                type="button"
                onClick={() => navigate('/attendance')}
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
              >
                الحضور
              </button>
              <button
                type="button"
                onClick={() => navigate('/daily-plan')}
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
              >
                خطة اليوم
              </button>
              <button
                type="button"
                onClick={() => navigate('/assessment')}
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 13 }}
              >
                التقييم
              </button>
              <button
                type="button"
                disabled
                style={{ padding: '6px 10px', borderRadius: 999, border: '1px solid #2563eb', background: '#2563eb', color: '#fff', cursor: 'default', fontSize: 13 }}
              >
                الرسوم
              </button>
            </div>
          </div>

          {email && (
            <p style={{ marginBottom: 8 }}>تم تسجيل الدخول بالبريد: <strong>{email}</strong></p>
          )}

          <div
            style={{
              marginBottom: 12,
              padding: 12,
              borderRadius: 12,
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              fontSize: 14,
            }}
          >
            <div style={{ marginBottom: 6 }}>
              <span style={{ marginInlineEnd: 12 }}>
                إجمالي الرسوم: <strong>{formatAmount(summary.totalFees)} IQD</strong>
              </span>
              <span style={{ marginInlineEnd: 12 }}>
                المدفوع: <strong>{formatAmount(summary.totalPaid)} IQD</strong>
              </span>
              <span>
                المتبقي: <strong>{formatAmount(summary.remaining)} IQD</strong>
              </span>
            </div>
            <div style={{ fontSize: 13, color: '#4b5563' }}>
              حالة الرسوم: <strong>{summary.status}</strong>
            </div>
          </div>

          {loading && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>جاري تحميل بيانات الرسوم...</div>
          )}

          {!loading && error && (
            <div style={{ color: '#b91c1c', marginTop: 16 }}>{error}</div>
          )}

          {!loading && !error && !hasFees && (
            <div style={{ marginTop: 16, fontSize: 14, color: '#6b7280' }}>
              لا توجد رسوم مسجلة لطفلك حتى الآن.
            </div>
          )}

          {!loading && !error && hasFees && (
            <div style={{ marginTop: 8 }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: 15 }}>تفاصيل الرسوم</h2>
              <div style={{ borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', background: '#fff' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead style={{ background: '#f9fafb' }}>
                      <tr>
                        <th style={{ textAlign: 'start', padding: '8px 10px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>نوع الرسم</th>
                        <th style={{ textAlign: 'start', padding: '8px 10px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>الوصف</th>
                        <th style={{ textAlign: 'start', padding: '8px 10px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>المبلغ (IQD)</th>
                        <th style={{ textAlign: 'start', padding: '8px 10px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>تاريخ الاستحقاق</th>
                        <th style={{ textAlign: 'start', padding: '8px 10px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>حالة الرسم</th>
                        <th style={{ textAlign: 'start', padding: '8px 10px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>المدفوع</th>
                        <th style={{ textAlign: 'start', padding: '8px 10px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>المتبقي</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fees.map(fee => {
                        const amount = fee.amount != null ? Number(fee.amount) || 0 : 0
                        const paidForFee = fee.total_paid_for_fee != null ? Number(fee.total_paid_for_fee) || 0 : 0
                        const remainingForFee = fee.remaining_for_fee != null ? Number(fee.remaining_for_fee) || 0 : 0
                        return (
                          <tr key={fee.fee_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#111827' }}>{feeTypeLabels[fee.fee_type] || fee.fee_type || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#111827' }}>{fee.description || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#111827' }}>{amount ? amount.toLocaleString('en-US') : '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#111827' }}>{fee.due_date || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#111827' }}>{feeStatusLabels[fee.status] || fee.status || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#111827' }}>{paidForFee ? paidForFee.toLocaleString('en-US') : '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#111827' }}>{remainingForFee ? remainingForFee.toLocaleString('en-US') : '—'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {!loading && !error && hasPayments && (
            <div style={{ marginTop: 20 }}>
              <h2 style={{ margin: '0 0 8px 0', fontSize: 15 }}>الدفعات المسجلة</h2>
              <div style={{ borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden', background: '#fff' }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                    <thead style={{ background: '#f9fafb' }}>
                      <tr>
                        <th style={{ textAlign: 'start', padding: '8px 10px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>تاريخ الدفع</th>
                        <th style={{ textAlign: 'start', padding: '8px 10px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>المبلغ (IQD)</th>
                        <th style={{ textAlign: 'start', padding: '8px 10px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>نوع الرسم</th>
                        <th style={{ textAlign: 'start', padding: '8px 10px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>طريقة الدفع</th>
                        <th style={{ textAlign: 'start', padding: '8px 10px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>المرجع</th>
                        <th style={{ textAlign: 'start', padding: '8px 10px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>ملاحظات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map(p => {
                        const amt = p.paid_amount != null ? Number(p.paid_amount) || 0 : 0
                        return (
                          <tr key={p.payment_id} style={{ borderTop: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#111827' }}>{p.paid_at ? new Date(p.paid_at).toLocaleString('ar-IQ') : '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#111827' }}>{amt ? amt.toLocaleString('en-US') : '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#111827' }}>{feeTypeLabels[p.fee_type] || p.fee_type || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#111827' }}>{p.payment_method || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#111827' }}>{p.reference || '—'}</td>
                            <td style={{ padding: '10px 12px', fontSize: 13, color: '#111827' }}>{p.notes || '—'}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
